/** Domain-aware building blocks shared across modules. */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { AlertTriangle, Search, UserRound, X } from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import { usePerms } from '@/lib/auth'
import { ageSex, bsDate, date, locale, money, time } from '@/lib/format'
import { cn, useDebounced } from '@/lib/utils'
import type { PatientSummary } from '@/api/types'
import { Avatar, Badge, Button } from '@/components/ui'

export function PatientChip({ p, link = true, className, size = 'md' }: { p: PatientSummary; link?: boolean; className?: string; size?: 'sm' | 'md' }) {
  const body = (
    <span className={cn('flex min-w-0 items-center gap-2.5', className)}>
      <Avatar name={p.full_name} className={size === 'sm' ? 'h-7 w-7 text-[11px]' : undefined} />
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-ink-900">
          {p.full_name}
          {p.name_local && <span className="nepali ml-1.5 font-normal text-ink-500">{p.name_local}</span>}
        </span>
        <span className="block truncate text-xs text-ink-500 tabular">
          {p.mrn} · {ageSex(p.age, p.sex)}
          {p.phone ? ` · ${p.phone}` : ''}
        </span>
      </span>
    </span>
  )
  return link ? (
    <Link to={`/app/patients/${p.id}`} className="block min-w-0 rounded-lg hover:opacity-80" onClick={(e) => e.stopPropagation()}>
      {body}
    </Link>
  ) : (
    body
  )
}

/** Async patient search (MRN, name, phone, Nepali name or identifier number). */
export function PatientPicker({
  value,
  onChange,
  label = 'Patient',
  autoFocus,
  error,
  inline,
  exclude = [],
  allowRegister = true,
  source,
}: {
  value: PatientSummary | null
  onChange: (p: PatientSummary | null) => void
  label?: string
  autoFocus?: boolean
  error?: string
  /** Show results in the page flow under the box (for dialogs, where a floating list would be cut off). */
  inline?: boolean
  /** Patient ids never offered (e.g. the record being merged into). */
  exclude?: string[]
  /** Offer "Register a new patient" when nothing matches (only to staff who may register patients). */
  allowRegister?: boolean
  /** `billing`: search billing accounts by name or MRN (billing.read), for staff without patients.read. */
  source?: 'billing'
}) {
  const { can } = usePerms()
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const dq = useDebounced(q, 200)
  const ref = useRef<HTMLDivElement>(null)
  const { data, isFetching, error: searchError } = useQuery({
    queryKey: ['patient-search', source, dq],
    queryFn: () => api.get<PatientSummary[]>(source === 'billing' ? '/billing/patients' : '/patients/search', { q: dq, limit: 8 }),
    enabled: dq.trim().length >= 2,
  })
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  if (value) {
    return (
      <div>
        {label && <span className="label">{label}</span>}
        <div className="flex items-center justify-between gap-2 rounded-xl border border-brand-200 bg-brand-50/50 px-3 py-2">
          <PatientChip p={value} link={false} />
          <Button variant="ghost" size="icon-sm" aria-label="Change patient" onClick={() => onChange(null)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    )
  }
  const results = (data ?? []).filter((p) => !exclude.includes(p.id))
  const listOpen = (open || !!inline) && dq.trim().length >= 2
  return (
    <div ref={ref} className="relative">
      {label && <label className="label" htmlFor="patient-picker">{label}</label>}
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-ink-500" />
        <input
          id="patient-picker"
          className="input pl-9"
          placeholder={source === 'billing' ? 'Search by name or MRN' : 'Search by name, MRN, phone or ID number'}
          value={q}
          autoFocus={autoFocus}
          aria-invalid={error ? true : undefined}
          autoComplete="off"
          role="combobox"
          aria-expanded={listOpen}
          aria-controls={listOpen ? "patient-picker-list" : undefined}
          onChange={(e) => {
            setQ(e.target.value)
            setOpen(true)
            setActive(0)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') setActive((a) => Math.min(a + 1, results.length - 1))
            if (e.key === 'ArrowUp') setActive((a) => Math.max(a - 1, 0))
            if (e.key === 'Enter' && results[active]) {
              e.preventDefault()
              onChange(results[active])
            }
          }}
        />
      </div>
      {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
      {listOpen && (
        <ul
          id="patient-picker-list"
          role="listbox"
          className={cn(
            'w-full overflow-auto rounded-xl border border-ink-200 bg-surface p-1',
            inline ? 'mt-2 max-h-[min(22rem,45vh)] divide-y divide-ink-100' : 'absolute z-30 mt-1 max-h-80 shadow-[var(--shadow-pop)]',
          )}
        >
          {isFetching && results.length === 0 && <li className="px-3 py-2 text-sm text-ink-500">Searching…</li>}
          {!isFetching && searchError && (
            <li className="px-3 py-3 text-sm text-rose-700">{searchError instanceof ApiError ? searchError.message : 'Search failed.'}</li>
          )}
          {!isFetching && !searchError && results.length === 0 && (
            <li className="px-3 py-3 text-sm text-ink-500">
              No match.
              {allowRegister && can('patients.write') && (
                <>
                  {' '}
                  <Link to="/app/patients/new" className="font-medium text-brand-700 hover:underline">
                    Register a new patient
                  </Link>
                </>
              )}
            </li>
          )}
          {results.map((p, i) => (
            <li
              key={p.id}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                e.preventDefault()
                onChange(p)
              }}
              className={cn('cursor-pointer rounded-lg px-2.5 py-2', i === active && 'bg-ink-100', inline && 'flex flex-wrap items-center justify-between gap-x-4 gap-y-1')}
            >
              <PatientChip p={p} link={false} />
              {inline && (
                <span className="text-xs text-ink-500">
                  {[p.municipality, p.district].filter(Boolean).join(', ') || 'No address'} · registered {date(p.created_at)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function Money({ value, className, strong }: { value: string | number | null | undefined; className?: string; strong?: boolean }) {
  return <span className={cn('tabular whitespace-nowrap', strong && 'font-semibold', className)}>{money(value)}</span>
}

/** Gregorian date with Bikram Sambat shown when the hospital enables it. */
export function DateBS({ value, className }: { value: string | null | undefined; className?: string }) {
  if (!value) return <span className="text-ink-500">—</span>
  const bs = locale.showBs ? bsDate(value) : null
  return (
    <span className={className}>
      {date(value)}
      {bs && <span className="ml-1.5 text-xs text-ink-500">({bs} BS)</span>}
    </span>
  )
}

export function AllergyBanner({ allergies }: { allergies: { id: string; allergen: string; severity: string; reaction?: string | null }[] }) {
  if (!allergies.length) {
    return (
      <Badge tone="neutral" icon={<UserRound className="h-3 w-3" />}>
        No known allergies recorded
      </Badge>
    )
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="note" aria-label="Allergies">
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700">
        <AlertTriangle className="h-3.5 w-3.5" /> Allergies:
      </span>
      {allergies.map((a) => (
        <Badge key={a.id} tone={a.severity === 'severe' ? 'critical' : 'serious'}>
          {a.allergen}
          {a.severity !== 'unknown' ? ` · ${a.severity}` : ''}
        </Badge>
      ))}
    </div>
  )
}

const PRESETS = [
  { label: 'Today', days: 0 },
  { label: '7 days', days: 6 },
  { label: '30 days', days: 29 },
  { label: '90 days', days: 89 },
]

export function DateRange({ from, to, onChange, className }: { from: string; to: string; onChange: (from: string, to: string) => void; className?: string }) {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: locale.timeZone }).format(new Date())
  const minus = (days: number) =>
    new Intl.DateTimeFormat('en-CA', { timeZone: locale.timeZone }).format(new Date(Date.now() - days * 86400000))
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)} role="group" aria-label="Date range">
      <div className="flex rounded-lg border border-ink-300 bg-surface p-0.5 shadow-xs">
        {PRESETS.map((p) => {
          const active = to === today && from === minus(p.days)
          return (
            <button
              key={p.label}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(minus(p.days), today)}
              className={cn('rounded-md px-2.5 py-1 text-[13px] font-medium', active ? 'bg-primary text-on-primary' : 'text-ink-600 hover:bg-ink-100')}
            >
              {p.label}
            </button>
          )
        })}
      </div>
      <div className="flex items-center gap-1.5 text-[13px] text-ink-500">
        <input type="date" aria-label="From" className="input h-8 w-[140px] py-1 text-[13px]" value={from} max={to} onChange={(e) => e.target.value && onChange(e.target.value, to)} />
        <span>to</span>
        <input type="date" aria-label="To" className="input h-8 w-[140px] py-1 text-[13px]" value={to} min={from} max={today} onChange={(e) => e.target.value && onChange(from, e.target.value)} />
      </div>
    </div>
  )
}

export function Stat({ label, value, sub, tone }: { label: string; value: ReactNode; sub?: ReactNode; tone?: 'critical' | 'warning' | 'good' }) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-ink-500">{label}</p>
      <p className={cn('mt-0.5 text-lg font-semibold tabular text-ink-900', tone === 'critical' && 'text-rose-700', tone === 'warning' && 'text-amber-700', tone === 'good' && 'text-emerald-700')}>
        {value}
      </p>
      {sub && <p className="text-xs text-ink-500">{sub}</p>}
    </div>
  )
}

interface OpenVisit { encounter_id: string; encounter_no: string; type: string; department_id: string | null; department: string | null; token_no: number | null; started_at: string }

/**
 * Front-desk warning: the patient already has an unfinished OPD/emergency visit today, with a link to it.
 * Clinicians open the consultation; front-desk staff open that department's queue (they cannot open
 * clinical records). `onOpenQueue` handles the queue link in place (e.g. inside the queue page itself);
 * otherwise `onNavigate` runs before leaving, so a drawer can close.
 */
export function OpenVisitWarning({ patientId, onOpenQueue, onNavigate }: { patientId: string | null | undefined; onOpenQueue?: (departmentId: string) => void; onNavigate?: () => void }) {
  const { can } = usePerms()
  const q = useQuery({
    queryKey: ['open-visits', patientId],
    enabled: !!patientId,
    queryFn: () => api.get<OpenVisit[]>('/visits/open', { patient_id: patientId! }),
  })
  if (!q.data?.length) return null
  const linkClass = 'cursor-pointer font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-950'
  const link = (v: OpenVisit) => {
    if (can('clinical.read')) return <Link to={`/app/encounters/${v.encounter_id}`} onClick={onNavigate} className={linkClass}>Open visit</Link>
    if (!v.department_id) return null
    const deptId = v.department_id
    return (
      <Link
        to={`/app/queue?dept=${deptId}`}
        onClick={(e) => {
          if (onOpenQueue) {
            e.preventDefault()
            onOpenQueue(deptId)
          } else onNavigate?.()
        }}
        className={linkClass}
      >
        Open in queue
      </Link>
    )
  }
  return (
    <div role="status" className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-900">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
      <div className="min-w-0">
        <p className="font-medium">Already has an open visit today</p>
        <ul className="mt-1 space-y-1">
          {q.data.map((v) => (
            <li key={v.encounter_id} className="flex flex-wrap items-baseline gap-x-2">
              <span>
                {v.encounter_no}
                {v.department ? ` · ${v.department}` : ''}
                {v.token_no ? ` · token ${v.token_no}` : ''} · since {time(v.started_at)}
              </span>
              {link(v)}
            </li>
          ))}
        </ul>
        <p className="mt-1.5">A new visit adds another consultation charge — continue only if this is a separate visit.</p>
      </div>
    </div>
  )
}
